import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Divider,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  NetworkCheck as NetworkIcon,
  Router as RouterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

const NetworkManagePage = () => {
  const [networks, setNetworks] = useState([]);
  const [availableBridges, setAvailableBridges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [networkVMs, setNetworkVMs] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  // 폼 상태
  const [formData, setFormData] = useState({
    name: '',
    network_type: 'bridge',
    bridge_name: '',
    cidr: '192.168.100.0/24',
    gateway: '',
    dns_servers: ['8.8.8.8', '8.8.4.4'],
    vlan_id: '',
    description: ''
  });

  useEffect(() => {
    loadNetworks();
    loadAvailableBridges();
  }, []);

  const loadNetworks = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNetworks();
      setNetworks(response.data.networks || []);
    } catch (error) {
      enqueueSnackbar('네트워크 목록 로딩 실패: ' + error.response?.data?.detail, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableBridges = async () => {
    try {
      const response = await apiService.getAvailableBridges();
      setAvailableBridges(response.data.bridges || []);
    } catch (error) {
      setAvailableBridges([]);
    }
  };

  const handleCreateNetwork = async () => {
    try {
      const networkData = {
        ...formData,
        dns_servers: formData.dns_servers.filter(dns => dns.trim() !== ''),
        vlan_id: formData.vlan_id === '' || isNaN(formData.vlan_id) ? null : parseInt(formData.vlan_id)
      };
      
      await apiService.createNetwork(networkData);
      enqueueSnackbar('네트워크가 생성되었습니다', { variant: 'success' });
      setCreateDialogOpen(false);
      resetForm();
      loadNetworks();
    } catch (error) {
      enqueueSnackbar('네트워크 생성 실패: ' + error.response?.data?.detail, { variant: 'error' });
    }
  };

  const handleEditNetwork = async (network) => {
    setSelectedNetwork(network);
    setFormData({
      name: network.name,
      network_type: network.network_type,
      bridge_name: network.bridge_name || '',
      cidr: network.cidr,
      gateway: network.gateway || '',
      dns_servers: network.dns_servers || ['8.8.8.8', '8.8.4.4'],
      vlan_id: network.vlan_id || '',
      description: network.description || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateNetwork = async () => {
    try {
      const networkData = {
        ...formData,
        dns_servers: formData.dns_servers.filter(dns => dns.trim() !== ''),
        vlan_id: formData.vlan_id === '' || isNaN(formData.vlan_id) ? null : parseInt(formData.vlan_id)
      };
      
      await apiService.updateNetwork(selectedNetwork.name, networkData);
      enqueueSnackbar('네트워크가 수정되었습니다', { variant: 'success' });
      setEditDialogOpen(false);
      resetForm();
      setSelectedNetwork(null);
      loadNetworks();
    } catch (error) {
      enqueueSnackbar('네트워크 수정 실패: ' + error.response?.data?.detail, { variant: 'error' });
    }
  };

  const handleDeleteNetwork = async (networkName) => {
    if (!window.confirm(`네트워크 '${networkName}'을 정말 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await apiService.deleteNetwork(networkName);
      enqueueSnackbar('네트워크가 삭제되었습니다', { variant: 'success' });
      loadNetworks();
    } catch (error) {
      enqueueSnackbar('네트워크 삭제 실패: ' + error.response?.data?.detail, { variant: 'error' });
    }
  };

  const handleViewNetworkVMs = async (network) => {
    try {
      setSelectedNetwork(network);
      const response = await apiService.getNetworkVMs(network.name);
      setNetworkVMs(response.data.vms || []);
      setViewDialogOpen(true);
    } catch (error) {
      enqueueSnackbar('네트워크 VM 목록 로딩 실패: ' + error.response?.data?.detail, { variant: 'error' });
    }
  };

  const handleUpdateDhcp = async (networkName) => {
    try {
      await apiService.updateNetworkDhcp(networkName);
      enqueueSnackbar(`네트워크 '${networkName}'의 DHCP 예약이 업데이트되었습니다`, { variant: 'success' });
      // VM 목록 새로고침
      if (selectedNetwork && selectedNetwork.name === networkName) {
        const response = await apiService.getNetworkVMs(networkName);
        setNetworkVMs(response.data.vms || []);
      }
    } catch (error) {
      enqueueSnackbar('DHCP 예약 업데이트 실패: ' + error.response?.data?.detail, { variant: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      network_type: 'bridge',
      bridge_name: '',
      cidr: '192.168.100.0/24',
      gateway: '',
      dns_servers: ['8.8.8.8', '8.8.4.4'],
      vlan_id: '',
      description: ''
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDNSChange = (index, value) => {
    const newDNS = [...formData.dns_servers];
    newDNS[index] = value;
    setFormData(prev => ({
      ...prev,
      dns_servers: newDNS
    }));
  };

  const addDNSServer = () => {
    setFormData(prev => ({
      ...prev,
      dns_servers: [...prev.dns_servers, '']
    }));
  };

  const removeDNSServer = (index) => {
    const newDNS = formData.dns_servers.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      dns_servers: newDNS
    }));
  };

  const getNetworkTypeChip = (type) => {
    const typeMap = {
      bridge: { label: 'Bridge', color: 'primary' },
      nat: { label: 'NAT', color: 'secondary' },
      isolated: { label: 'Isolated', color: 'warning' },
      'host-only': { label: 'Host-Only', color: 'info' }
    };
    const config = typeMap[type] || { label: type, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <NetworkIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            네트워크 관리
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* 네트워크 통계 카드 */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RouterIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6">총 네트워크</Typography>
                    <Typography variant="h4" color="primary">
                      {networks.length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NetworkIcon sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h6">활성 네트워크</Typography>
                    <Typography variant="h4" color="success.main">
                      {networks.filter(n => n.enabled).length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NetworkIcon sx={{ mr: 2, fontSize: 40, color: 'info.main' }} />
                  <Box>
                    <Typography variant="h6">브리지 네트워크</Typography>
                    <Typography variant="h4" color="info.main">
                      {networks.filter(n => n.network_type === 'bridge').length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 네트워크 목록 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">네트워크 프로필</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      resetForm();
                      setCreateDialogOpen(true);
                    }}
                  >
                    네트워크 생성
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography>로딩 중...</Typography>
                  </Box>
                ) : networks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">생성된 네트워크가 없습니다</Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>이름</TableCell>
                          <TableCell>타입</TableCell>
                          <TableCell>CIDR</TableCell>
                          <TableCell>게이트웨이</TableCell>
                          <TableCell>브리지</TableCell>
                          <TableCell>상태</TableCell>
                          <TableCell>작업</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {networks.map((network) => (
                          <TableRow key={network.name}>
                            <TableCell>
                              <Box>
                                <Typography variant="subtitle2">{network.name}</Typography>
                                {network.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {network.description}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{getNetworkTypeChip(network.network_type)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {network.cidr}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {network.gateway}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {network.bridge_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={network.enabled ? '활성' : '비활성'}
                                color={network.enabled ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Tooltip title="VM 목록 보기">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewNetworkVMs(network)}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </Tooltip>
                                {(network.network_type === 'nat' || network.network_type === 'isolated') && (
                                  <Tooltip title="DHCP 예약 업데이트">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleUpdateDhcp(network.name)}
                                      color="primary"
                                    >
                                      <RefreshIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="편집">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditNetwork(network)}
                                    color="primary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                {network.name !== 'default' && (
                                  <Tooltip title="삭제">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteNetwork(network.name)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 네트워크 생성 다이얼로그 */}
        <Dialog open={createDialogOpen} onClose={() => {
          setCreateDialogOpen(false);
          resetForm();
        }} maxWidth="md" fullWidth>
          <DialogTitle>새 네트워크 생성</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="네트워크 이름"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>네트워크 타입</InputLabel>
                  <Select
                    value={formData.network_type}
                    onChange={(e) => handleFormChange('network_type', e.target.value)}
                    label="네트워크 타입"
                  >
                    <MenuItem value="bridge">Bridge (외부 물리 브리지)</MenuItem>
                    <MenuItem value="nat">NAT (인터넷 공유)</MenuItem>
                    <MenuItem value="isolated">격리 네트워크</MenuItem>
                    <MenuItem value="host-only">Host-Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* 브리지 이름 필드 (bridge 타입인 경우만 표시) */}
              {formData.network_type === 'bridge' && (
                <>
                  <Grid item xs={12} md={6}>
                    {availableBridges.length > 0 ? (
                      <FormControl fullWidth required>
                        <InputLabel>브리지 장치</InputLabel>
                        <Select
                          value={formData.bridge_name}
                          onChange={(e) => handleFormChange('bridge_name', e.target.value)}
                          label="브리지 장치"
                        >
                          {availableBridges.map((bridge) => (
                            <MenuItem key={bridge} value={bridge}>
                              {bridge}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        label="브리지 이름"
                        value={formData.bridge_name}
                        onChange={(e) => handleFormChange('bridge_name', e.target.value)}
                        required
                        helperText="시스템에서 브리지를 찾을 수 없습니다. 직접 입력하세요"
                        error
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      🌐 <strong>Bridge 모드 안내:</strong> 물리 네트워크에 직접 연결되어 외부 DHCP 서버가 IP를 관리합니다. 
                      libvirt는 단순히 브리지 연결만 제공하며, DHCP 예약이나 IP 관리 기능은 사용할 수 없습니다.
                      {availableBridges.length > 0 && (
                        <><br />✅ <strong>발견된 브리지:</strong> {availableBridges.join(', ')}</>
                      )}
                    </Alert>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CIDR (예: 192.168.100.0/24)"
                  value={formData.cidr}
                  onChange={(e) => handleFormChange('cidr', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="게이트웨이 (선택사항)"
                  value={formData.gateway}
                  onChange={(e) => handleFormChange('gateway', e.target.value)}
                  helperText="비워두면 자동으로 설정됩니다"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="VLAN ID (선택사항)"
                  type="number"
                  value={formData.vlan_id}
                  onChange={(e) => handleFormChange('vlan_id', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="설명"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>DNS 서버</Typography>
                {formData.dns_servers.map((dns, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`DNS 서버 ${index + 1}`}
                      value={dns}
                      onChange={(e) => handleDNSChange(index, e.target.value)}
                      sx={{ mr: 1 }}
                    />
                    {formData.dns_servers.length > 1 && (
                      <Button onClick={() => removeDNSServer(index)} color="error">
                        삭제
                      </Button>
                    )}
                  </Box>
                ))}
                <Button onClick={addDNSServer} variant="outlined" size="small">
                  DNS 서버 추가
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCreateDialogOpen(false);
              resetForm();
            }}>취소</Button>
            <Button onClick={handleCreateNetwork} variant="contained" disabled={!formData.name}>
              생성
            </Button>
          </DialogActions>
        </Dialog>

        {/* 네트워크 편집 다이얼로그 */}
        <Dialog open={editDialogOpen} onClose={() => {
          setEditDialogOpen(false);
          resetForm();
          setSelectedNetwork(null);
        }} maxWidth="md" fullWidth>
          <DialogTitle>네트워크 '{selectedNetwork?.name}' 편집</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="네트워크 이름"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                  disabled={selectedNetwork?.name === 'default'}
                  helperText={selectedNetwork?.name === 'default' ? '기본 네트워크의 이름은 변경할 수 없습니다' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>네트워크 타입</InputLabel>
                  <Select
                    value={formData.network_type}
                    onChange={(e) => handleFormChange('network_type', e.target.value)}
                    label="네트워크 타입"
                  >
                    <MenuItem value="bridge">Bridge (외부 물리 브리지)</MenuItem>
                    <MenuItem value="nat">NAT (인터넷 공유)</MenuItem>
                    <MenuItem value="isolated">격리 네트워크</MenuItem>
                    <MenuItem value="host-only">Host-Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* 브리지 이름 필드 (bridge 타입인 경우만 표시) */}
              {formData.network_type === 'bridge' && (
                <>
                  <Grid item xs={12} md={6}>
                    {availableBridges.length > 0 ? (
                      <FormControl fullWidth required>
                        <InputLabel>브리지 장치</InputLabel>
                        <Select
                          value={formData.bridge_name}
                          onChange={(e) => handleFormChange('bridge_name', e.target.value)}
                          label="브리지 장치"
                        >
                          {availableBridges.map((bridge) => (
                            <MenuItem key={bridge} value={bridge}>
                              {bridge}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        label="브리지 이름"
                        value={formData.bridge_name}
                        onChange={(e) => handleFormChange('bridge_name', e.target.value)}
                        required
                        helperText="시스템에서 브리지를 찾을 수 없습니다. 직접 입력하세요"
                        error
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      🌐 <strong>Bridge 모드 안내:</strong> 물리 네트워크에 직접 연결되어 외부 DHCP 서버가 IP를 관리합니다. 
                      libvirt는 단순히 브리지 연결만 제공하며, DHCP 예약이나 IP 관리 기능은 사용할 수 없습니다.
                      {availableBridges.length > 0 && (
                        <><br />✅ <strong>발견된 브리지:</strong> {availableBridges.join(', ')}</>
                      )}
                    </Alert>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CIDR (예: 192.168.100.0/24)"
                  value={formData.cidr}
                  onChange={(e) => handleFormChange('cidr', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="게이트웨이 (선택사항)"
                  value={formData.gateway}
                  onChange={(e) => handleFormChange('gateway', e.target.value)}
                  helperText="비워두면 자동으로 설정됩니다"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="VLAN ID (선택사항)"
                  type="number"
                  value={formData.vlan_id}
                  onChange={(e) => handleFormChange('vlan_id', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="설명"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>DNS 서버</Typography>
                {formData.dns_servers.map((dns, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`DNS 서버 ${index + 1}`}
                      value={dns}
                      onChange={(e) => handleDNSChange(index, e.target.value)}
                      sx={{ mr: 1 }}
                    />
                    {formData.dns_servers.length > 1 && (
                      <Button onClick={() => removeDNSServer(index)} color="error">
                        삭제
                      </Button>
                    )}
                  </Box>
                ))}
                <Button onClick={addDNSServer} variant="outlined" size="small">
                  DNS 서버 추가
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditDialogOpen(false);
              resetForm();
              setSelectedNetwork(null);
            }}>취소</Button>
            <Button onClick={handleUpdateNetwork} variant="contained" disabled={!formData.name}>
              수정
            </Button>
          </DialogActions>
        </Dialog>

        {/* 네트워크 VM 목록 다이얼로그 */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            네트워크 '{selectedNetwork?.name}' - 연결된 VM 목록
          </DialogTitle>
          <DialogContent>
            {networkVMs.length === 0 ? (
              <Alert severity="info">이 네트워크에 연결된 VM이 없습니다.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>VM 이름</TableCell>
                      <TableCell>IP 주소</TableCell>
                      <TableCell>MAC 주소</TableCell>
                      <TableCell>호스트명</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {networkVMs.map((vm, index) => (
                      <TableRow key={index}>
                        <TableCell>{vm.vm_name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {(() => {
                            const configuredIP = vm.interface?.ip_address;
                            const actualIP = vm.interface?.actual_ip_address;
                            
                            if (!configuredIP && !actualIP) {
                              return 'N/A';
                            }
                            
                            // 실제 IP가 있고 설정 IP와 다른 경우
                            if (actualIP && configuredIP && actualIP !== configuredIP) {
                              return (
                                <Tooltip title={`설정 IP: ${configuredIP}`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#4caf50' }}>{actualIP}</span>
                                    <Chip 
                                      label="실제" 
                                      size="small" 
                                      color="success" 
                                      sx={{ ml: 1, fontSize: '0.7rem', height: '18px' }}
                                    />
                                  </Box>
                                </Tooltip>
                              );
                            }
                            
                            // 실제 IP가 있는 경우 (설정 IP와 같거나 설정 IP가 없는 경우)
                            if (actualIP) {
                              return actualIP;
                            }
                            
                            // 설정 IP만 있는 경우 (VM이 실행되지 않음)
                            return (
                              <Tooltip title="VM이 실행되지 않음">
                                <span style={{ color: '#ff9800' }}>{configuredIP}</span>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {vm.interface?.mac_address || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {vm.interface?.hostname || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            {(selectedNetwork?.network_type === 'nat' || selectedNetwork?.network_type === 'isolated') && (
              <Button
                onClick={() => handleUpdateDhcp(selectedNetwork.name)}
                startIcon={<RefreshIcon />}
                color="primary"
              >
                DHCP 예약 업데이트
              </Button>
            )}
            <Button onClick={() => setViewDialogOpen(false)}>닫기</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default NetworkManagePage; 